import type { SupabaseClient } from '@supabase/supabase-js';

const ASSURANCE_LEVEL_1 = 'aal1';
const ASSURANCE_LEVEL_2 = 'aal2';

/**
 * @name decodeAalFromJwt
 * @description Liest den `aal`-Claim aus dem Payload eines Supabase-Access-Tokens.
 * Lokaler Helper, weil auth-js' `decodeJWT` nicht public exportiert ist.
 * Gibt 'aal1' | 'aal2' zurück, oder null bei fehlendem/malformed Token bzw.
 * fehlendem aal-Claim. Reine Funktion (kein Netz, keine Seiteneffekte) →
 * unit-testbar.
 */
export function decodeAalFromJwt(accessToken: string): 'aal1' | 'aal2' | null {
  const parts = accessToken.split('.');

  if (parts.length !== 3) {
    return null;
  }

  const payloadSegment = parts[1];

  if (!payloadSegment) {
    return null;
  }

  try {
    // base64url → base64, dann Edge-kompatibel dekodieren.
    // KEIN Buffer: die Next.js-Middleware läuft in der Edge-Runtime, wo Buffer
    // nicht existiert. atob + TextDecoder sind in Edge UND Node verfügbar.
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    const payload = JSON.parse(json) as { aal?: unknown };

    if (
      payload.aal === ASSURANCE_LEVEL_1 ||
      payload.aal === ASSURANCE_LEVEL_2
    ) {
      return payload.aal;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * @name checkRequiresMultiFactorAuthentication
 * @description Checks if the current session requires multi-factor authentication.
 * We do it by checking that the next assurance level is AAL2 and that the current
 * assurance level is not AAL2.
 *
 * Datenquelle: die DB-Funktion is_mfa_compliant() (13-mfa.sql) per RPC – exakt
 * die Funktion, die auch die RLS nutzt. Sie liest auth.mfa_factors LIVE und gibt
 * false zurück, sobald ein verifizierter Faktor existiert, die Session aber noch
 * nicht aal2 ist → genau dann ist MFA nötig. Damit ist das Gate by design
 * konsistent mit der DB-RLS.
 *
 * Warum nicht getAuthenticatorAssuranceLevel()/getUser(): beide sind in der
 * Edge-Middleware unzuverlässig. getAuthenticatorAssuranceLevel() ohne jwt-Arg
 * liest den Cookie-Snapshot (übersieht frisch enrollte Faktoren → PGRST116-Crash,
 * #MFA-Signup-Crash 2026-06-11). getUser() ohne Arg wirft AuthSessionMissingError
 * (kein initialisierter Session-State im per-Request Middleware-Client),
 * getUser(jwt) scheitert bei Token-Lifecycle. Der RPC läuft über PostgREST
 * (Edge-safe) und nutzt das Bearer-Token des Clients direkt.
 *
 * NOTE: If MFA factors were deleted (e.g., via recovery code login), the JWT may
 * still indicate AAL2; the "no factors available" state is handled in the
 * FactorsListContainer component which redirects appropriately.
 *
 * Early-Out: ist die Session bereits aal2, wird kein DB-Call ausgelöst.
 *
 * @param client
 */
export async function checkRequiresMultiFactorAuthentication(
  client: SupabaseClient,
) {
  return (await readMfaGate(client)) !== 'none';
}

/**
 * @name readMfaGate
 * @description Wohin eine Sitzung gehoert, in EINEM Datenbankaufruf:
 * - `'none'`   — keine Sitzung, oder schon aal2, oder (fail-open) unklar.
 * - `'verify'` — verifizierter Faktor, Sitzung aal1 ⇒ `/auth/verify`.
 * - `'enroll'` — Sitzung aal1 und KEIN verifizierter Faktor.
 *
 * 🔴 `'enroll'` gibt es, weil `is_mfa_compliant()` fuer ein Konto OHNE Faktor
 * bei aal1 WAHR ist (`13-mfa.sql`). Wer nur `'verify'` prueft, laesst ein
 * solches Konto dauerhaft mit dem Passwort allein hinein — gemessen am
 * 05.10.2026: 5 bestaetigte Konten in Produktion, 3 auf Staging; und nach
 * jedem Admin-MFA-Reset bis dahin auch das zurueckgesetzte. Die Middleware
 * leitet `'enroll'` nach `/auth/verify`; dort legt `sendMfaCodeAction` den
 * Faktor an und verlangt eine neue Anmeldung (#1322 — die Enrollment-Sitzung
 * wird NICHT uebernommen).
 *
 * `checkRequiresMultiFactorAuthentication` ist `!== 'none'` — auch
 * `requireUser` (Server-Aktionen, Routen) schickt ein Konto ohne Faktor nach
 * `/auth/verify`, statt es mit aal1 durchzulassen.
 */
export async function readMfaGate(
  client: SupabaseClient,
): Promise<'none' | 'verify' | 'enroll'> {
  // Suppress the getSession warning. Remove when the issue is fixed.
  // https://github.com/supabase/auth-js/issues/873
  // @ts-expect-error: suppressGetSessionWarning is not part of the public API
  client.auth.suppressGetSessionWarning = true;

  const {
    data: { session },
  } = await client.auth.getSession();

  // @ts-expect-error: suppressGetSessionWarning is not part of the public API
  client.auth.suppressGetSessionWarning = false;

  if (!session) {
    return 'none';
  }

  // Early-Out: Session bereits aal2 → kein MFA nötig, kein DB-Call.
  // currentLevel aus dem JWT (aal-Claim), deckungsgleich mit auth.jwt()->>'aal'
  // in is_mfa_compliant().
  const aal = decodeAalFromJwt(session.access_token);

  if (aal === ASSURANCE_LEVEL_2) {
    return 'none';
  }

  // Live-Wahrheit aus der DB: is_mfa_compliant() ist die RLS-Funktion selbst.
  // false = verifizierter Faktor vorhanden, Session aber < aal2 → MFA nötig.
  const { data: compliant, error } = await client.rpc('is_mfa_compliant');

  // fail-closed statt throw: ein Fehler hier (z.B. transienter RPC-Blip, 401 bei
  // Token-Lifecycle) darf in der Edge-Middleware NIEMALS einen 500 erzeugen
  // (Lehre aus den Edge-Crashes 2026-06-11). Stattdessen zur MFA/Re-Auth leiten –
  // der schlimmste Ausgang ist ein überflüssiger /auth/verify-Redirect (dort
  // fängt FactorsListContainer "keine Faktoren" ab), kein Crash, kein Datenleck.
  if (error) {
    return 'verify';
  }

  if (compliant === false) {
    return 'verify';
  }

  // Wahr bei aal1 heisst: kein verifizierter Faktor. Nur bei einem LESBAREN
  // aal1-Anspruch — ein fehlender/kaputter Anspruch bleibt fail-open.
  if (compliant === true && aal === ASSURANCE_LEVEL_1) {
    return 'enroll';
  }

  // null/undefined (theoretisch unmöglich, is_mfa_compliant gibt nie NULL) →
  // fail-open: kein MFA-Zwang, aber die DB-RLS bleibt die echte
  // Schutzschicht für aal2-Daten.
  return 'none';
}
